import {
  Card,
  DetailPageContent,
  Form,
  FutureDetailPage,
  LoadingScreen,
  useFormValues,
} from "@administrate/piston-ux";
import { DetailPageOverview } from "@administrate/piston-ux/lib/layouts/DetailPage/DetailPageOverview";
import moment from "moment";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ItemInformation } from "../../components/ItemInformation";
import {
  EventBooking,
  EventBookingType,
  FilterOperation,
  LearnerField,
  Query,
} from "../../generated/weblinkTypes";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import { useWebLinkQuery } from "../../hooks/weblink";
import { WEBLINK_BOOKING_BY_ID_QUERY } from "../../queries/bookings";
import { BookingQueryVariables } from "../../types/Bookings";
import { useHistory, useParams } from "../../useHistory";
import {
  mergeLearnerResults,
  updateFormLearners,
} from "../../utils/bookingHelpers";
import { formatDateRange } from "../../utils/dateTimeHelpers";
import { BookingLearnersForm } from "./BookingLearnersForm";
import {
  BookingLearnersFilters,
  BookingLearnersHeader,
} from "./BookingLearnersHeader";
import { CancelLearnerModal, LearnerCategory } from "./CancelLearnerModal";
import { TransferLearnerModal } from "./TransferLearnerModal";

export const DEFAULT_EVENT: EventBooking = {
  id: "",
  name: "",
  start: "",
  end: "",
  type: EventBookingType.Public,
  imageUrl: "",
  bookedPlaces: 0,
  remainingPlaces: 0,
  unnamedLearnerQuantity: 0,
  namedLearnerQuantity: 0,
  price: { amount: 0, financialUnit: { name: "", code: "", symbol: "" } },
  learners: {
    edges: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      totalRecords: 0,
      startCursor: "",
      endCursor: "",
    },
  },
  instructors: [],
  __typename: "EventBooking",
};

const DEFAULT_NUM_LEARNERS = 50;

export const ExistingEventBooking: FunctionComponent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { bookingId } = useParams<{ bookingId: string }>();

  const [cancellingLearnerId, setCancellingLearnerId] = useState<string | null>(
    null,
  );
  const [transferringLearnerId, setTransferringLearnerId] = useState<
    string | null
  >(null);

  const [headerFilters, setHeaderFilters] = useState<BookingLearnersFilters>({
    search: "",
    learnerStatus: undefined,
  });
  const filterChange = useCallback(
    filters => setHeaderFilters(filters),
    [setHeaderFilters],
  );

  const queryVariables = useCallback(
    (learnerOffset?: number) => {
      const variables: BookingQueryVariables = {
        bookingId,
        noOfLearners: DEFAULT_NUM_LEARNERS,
        learnerOffset: learnerOffset ? learnerOffset : 0,
        learnerFilters: [],
      };

      const { search, learnerStatus, learnerType } = headerFilters;

      if (search) {
        variables.learnerFilters.push({
          field: LearnerField.ContactName,
          operation: FilterOperation.Like,
          value: `%${search}%`,
        });
      }
      if (learnerStatus) {
        const { value } = learnerStatus;

        if (value === "active") {
          variables.learnerFilters.push({
            field: LearnerField.IsCancelled,
            operation: FilterOperation.Eq,
            value: "false",
          });
        }
        if (value === "cancelled") {
          variables.learnerFilters.push({
            field: LearnerField.IsCancelled,
            operation: FilterOperation.Eq,
            value: "true",
          });
        }
      }
      if (learnerType) {
        const { value } = learnerType;
        if (value === "named") {
          variables.learnerFilters.push({
            field: LearnerField.IsUnnamed,
            operation: FilterOperation.Eq,
            value: "false",
          });
        }
        if (value === "unnamed") {
          variables.learnerFilters.push({
            field: LearnerField.IsUnnamed,
            operation: FilterOperation.Eq,
            value: "true",
          });
        }
      }

      return variables;
    },
    [headerFilters, bookingId],
  );

  const {
    loading,
    data,
    refetch: refetchBooking,
    fetchMore,
    error,
  } = useWebLinkQuery<Query>(WEBLINK_BOOKING_BY_ID_QUERY, {
    variables: queryVariables(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const booking =
    data?.viewer?.eventBookings?.edges?.[0]?.node || DEFAULT_EVENT;

  const fetchMoreLearners = (learnerOffset: number) => {
    fetchMore({
      variables: queryVariables(learnerOffset),
      updateQuery: (prev, { fetchMoreResult }) =>
        // TODO: Fix types
        mergeLearnerResults(
          "eventBookings",
          prev as any,
          fetchMoreResult as any,
        ),
    });
  };

  const { dateFormat } = useDateFormatter({ showTime: true });

  const firstBooking = data?.viewer?.eventBookings?.edges?.[0]?.node;

  const maxLearners = firstBooking?.maxPlaces || null;
  const bookedPlaces = firstBooking?.bookedPlaces || 0;
  const totalLearnerRecords =
    firstBooking?.learners?.pageInfo?.totalRecords || 0;

  const canAddMoreLearners = () => {
    // TODO: This should go away after we implement editing for all types
    if (booking.type !== EventBookingType.Private) {
      return false;
    }

    if (booking.end) {
      const end = moment(booking.end);
      if (moment().isAfter(end)) {
        return false;
      }
    }

    return (
      maxLearners === null ||
      maxLearners === undefined ||
      bookedPlaces < maxLearners
    );
  };

  const pointOfSaleFields = (data?.pointOfSaleLearnerFields || []).filter(
    ({ courseTemplates }) =>
      !courseTemplates.length ||
      (firstBooking?.course?.id &&
        courseTemplates.includes(firstBooking.course.id)),
  );

  const mappedPosFields = pointOfSaleFields.filter(field => field.mappedField);

  const values = useFormValues({
    learners: [],
  });

  useEffect(() => {
    const learners = firstBooking?.learners?.edges || [];
    updateFormLearners(learners, values, mappedPosFields);
  }, [firstBooking, mappedPosFields, values]);

  // Since we re-use the item query to fetch more learners, we need to avoid putting the item details into a loading
  // state each time
  const isLoadingItemDetails = loading && !data;
  if (isLoadingItemDetails) {
    return <LoadingScreen />;
  }

  return (
    <>
      <FutureDetailPage
        back={{
          label: t("bookings"),
          onClick: () => history.goBack(),
        }}
        title={!isLoadingItemDetails ? booking.name : ""}
        legacyId={bookingId}
        detailType="Booking"
        form={{
          saveState: null,
        }}
        page="overview"
        layout="adjacent"
      >
        <DetailPageOverview>
          <ItemInformation
            data={{
              location: booking.location ? booking.location : undefined,
              dateAndTime: booking.start
                ? formatDateRange(booking.start, booking.end || "", dateFormat)
                : undefined,
              instructors: booking.instructors,
            }}
            loading={isLoadingItemDetails}
            name={booking.type === "private" ? "privateCourse" : "publicCourse"}
            image={booking.imageUrl}
          />
        </DetailPageOverview>
        <DetailPageContent>
          <Form type="raw" values={values}>
            <Card>
              <BookingLearnersHeader
                loading={isLoadingItemDetails}
                onChange={filterChange}
                type="Event"
                booking={booking}
                canAddMoreLearners={canAddMoreLearners}
              />
              <BookingLearnersForm
                values={values}
                mappedPosFields={mappedPosFields}
                totalNoOfLearners={totalLearnerRecords}
                fetchMoreLearners={fetchMoreLearners}
                error={error}
                isEvent={true}
                loading={loading}
                onCancelLearnerClick={setCancellingLearnerId}
                onTransferLearnerClick={setTransferringLearnerId}
              />
            </Card>
          </Form>
        </DetailPageContent>
      </FutureDetailPage>
      {!!cancellingLearnerId && (
        <CancelLearnerModal
          onModalClose={submitted => {
            if (submitted) {
              refetchBooking();
            }
            setCancellingLearnerId(null);
          }}
          learnerId={cancellingLearnerId}
          learnerCategory={LearnerCategory.Event}
        />
      )}
      {!!transferringLearnerId && (
        <TransferLearnerModal
          onModalClose={submitted => {
            if (submitted) {
              refetchBooking();
            }
            setTransferringLearnerId(null);
          }}
          learnerId={transferringLearnerId}
          bookingId={booking.id}
        />
      )}
    </>
  );
};
